import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { getTranscriptById, getTranscriptUrl } from '../modules/api/zoom';
import { logger, LogCategory } from '../services/logger';
import { ParsedTranscript, TranscriptSegment, TranscriptMetadata, TranscriptUrlResponse } from '../types/transcript';

interface UseTranscriptReturn {
    metadata: TranscriptMetadata | null;
    transcriptContent: ParsedTranscript | null;
    isLoading: boolean;
    hasTranscript: boolean;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    searchResults: TranscriptSegment[];
    exportTranscript: (format: string) => void;
    error: Error | null;
}

export const useTranscript = (recordingId: string, enabled: boolean = true, meetingTitle?: string): UseTranscriptReturn => {
    const [transcriptContent, setTranscriptContent] = useState<ParsedTranscript | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<TranscriptSegment[]>([]);

    useEffect(() => {
        if (enabled) {
            logger.info(
                LogCategory.TRANSCRIPT_UI,
                'TRANSCRIPT_HOOK_INIT',
                `Initializing transcript hook for recording ${recordingId}`,
                { recordingId, enabled }
            );
        }
    }, [recordingId, enabled]);

    const { data: metadata, isLoading: metadataLoading, error: metadataError } = useQuery<TranscriptMetadata | null>(
        ['transcript-metadata', recordingId],
        () => getTranscriptById(recordingId),
        {
            enabled,
            onSuccess: (data) => {
                logger.info(
                    LogCategory.TRANSCRIPT_UI,
                    'TRANSCRIPT_METADATA_LOADED',
                    'Transcript metadata loaded successfully',
                    {
                        recordingId,
                        hasTranscript: data?.hasTranscript,
                        format: data?.transcriptFormat
                    }
                );
            },
            onError: (error) => {
                logger.error(
                    LogCategory.TRANSCRIPT_UI,
                    'TRANSCRIPT_METADATA_ERROR',
                    'Failed to load transcript metadata',
                    error as Error,
                    { recordingId }
                );
            }
        }
    );

    const { data: urlData, isLoading: urlLoading } = useQuery<TranscriptUrlResponse | null>(
        ['transcript-url', recordingId],
        () => getTranscriptUrl(recordingId),
        { 
            enabled: enabled && metadata?.hasTranscript === true,
            staleTime: 1000 * 60 * 30
        }
    );

    // Load real transcript content when available
    useEffect(() => {
        if (metadata?.hasTranscript && !transcriptContent && urlData?.signedUrl) {
            // Fetch actual transcript content from the signed URL
            fetch(urlData.signedUrl)
                .then(response => response.text())
                .then(content => {
                    try {
                        const parsed = parseVTTContent(content, metadata.transcriptFormat);
                        setTranscriptContent(parsed);
                    } catch (error) {
                        logger.error(
                            LogCategory.TRANSCRIPT_UI,
                            'TRANSCRIPT_PARSE_ERROR',
                            'Failed to parse transcript content',
                            error as Error,
                            { recordingId }
                        );
                    }
                })
                .catch(error => {
                    logger.error(
                        LogCategory.TRANSCRIPT_UI,
                        'TRANSCRIPT_FETCH_ERROR',
                        'Failed to fetch transcript content',
                        error as Error,
                        { recordingId, signedUrl: urlData.signedUrl }
                    );
                });
        }
    }, [metadata, urlData, transcriptContent, recordingId]);

    const handleSearch = useCallback((query: string) => {
        const searchStartTime = performance.now();
        setSearchQuery(query);
        
        if (query && transcriptContent) {
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'TRANSCRIPT_SEARCH',
                `User searching for: "${query}"`,
                {
                    recordingId,
                    query,
                    queryLength: query.length
                }
            );
            
            const results = transcriptContent.segments.filter(segment =>
                segment.text.toLowerCase().includes(query.toLowerCase())
            );
            
            const searchTime = performance.now() - searchStartTime;
            
            logger.metric(
                LogCategory.TRANSCRIPT_PERFORMANCE,
                'SEARCH_PERFORMANCE',
                { duration: searchTime },
                {
                    recordingId,
                    query,
                    resultCount: results.length,
                    totalSegments: transcriptContent.segments.length
                }
            );
            
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    }, [transcriptContent, recordingId]);

    const exportTranscript = useCallback((format: string) => {
        logger.info(
            LogCategory.TRANSCRIPT_USER_ACTION,
            'TRANSCRIPT_EXPORT',
            `User exporting transcript as ${format}`,
            {
                recordingId,
                format,
                segmentCount: transcriptContent?.segments.length,
                contentSize: transcriptContent?.fullText.length
            }
        );
        
        try {
            const exportStartTime = performance.now();
            
            if (!transcriptContent) {
                throw new Error('No transcript content available for export');
            }

            let content = '';
            // Create a clean filename from meeting title
            const cleanTitle = meetingTitle 
                ? meetingTitle.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').slice(0, 50)
                : `recording_${recordingId}`;
            let filename = `${cleanTitle}_transcript`;
            let mimeType = 'text/plain';

            switch (format.toLowerCase()) {
                case 'txt':
                    // Create formatted text with timestamps and speakers
                    const header = `Meeting Transcript: ${meetingTitle || 'Recording ' + recordingId}\n`;
                    const dateHeader = `Generated: ${new Date().toLocaleString()}\n`;
                    const separator = '='.repeat(60) + '\n\n';
                    
                    const formattedContent = transcriptContent.segments
                        .map(segment => {
                            const timestamp = formatTimestamp(segment.startTime);
                            const speaker = segment.speaker ? `${segment.speaker}: ` : '';
                            return `[${timestamp}] ${speaker}${segment.text}`;
                        })
                        .join('\n\n');
                    
                    content = header + dateHeader + separator + formattedContent;
                    filename += '.txt';
                    mimeType = 'text/plain';
                    break;
                    
                case 'srt':
                    content = transcriptContent.segments
                        .map((segment, index) => {
                            const start = formatSRTTime(segment.startTime);
                            const end = formatSRTTime(segment.endTime);
                            return `${index + 1}\n${start} --> ${end}\n${segment.text}\n`;
                        })
                        .join('\n');
                    filename += '.srt';
                    mimeType = 'text/plain';
                    break;
                    
                case 'json':
                    content = JSON.stringify(transcriptContent, null, 2);
                    filename += '.json';
                    mimeType = 'application/json';
                    break;
                    
                default:
                    content = transcriptContent.fullText;
                    filename += '.txt';
                    mimeType = 'text/plain';
            }

            const blob = new Blob([content], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            const exportTime = performance.now() - exportStartTime;
            
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'TRANSCRIPT_EXPORT_SUCCESS',
                `Transcript exported successfully in ${exportTime}ms`,
                {
                    recordingId,
                    format,
                    exportTime,
                    filename,
                    contentLength: content.length
                }
            );
            
            logger.metric(
                LogCategory.TRANSCRIPT_PERFORMANCE,
                'EXPORT_PERFORMANCE',
                { duration: exportTime },
                { recordingId, format, fileSize: content.length }
            );
        } catch (error) {
            logger.error(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'TRANSCRIPT_EXPORT_ERROR',
                'Failed to export transcript',
                error as Error,
                { recordingId, format }
            );
        }
    }, [transcriptContent, recordingId]);

    return {
        metadata,
        transcriptContent,
        isLoading: metadataLoading || urlLoading,
        hasTranscript: metadata?.hasTranscript || false,
        searchQuery,
        setSearchQuery: handleSearch,
        searchResults,
        exportTranscript,
        error: metadataError as Error | null,
    };
};

function parseVTTContent(content: string, format: string): ParsedTranscript {
    const segments: TranscriptSegment[] = [];
    const speakers: Set<string> = new Set();
    let duration = 0;
    
    if (format === 'vtt') {
        const lines = content.split('\n');
        let currentSegment: Partial<TranscriptSegment> = {};
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            
            if (line.includes('-->')) {
                const [start, end] = line.split('-->').map(t => parseVTTTime(t.trim()));
                currentSegment.startTime = start;
                currentSegment.endTime = end;
                duration = Math.max(duration, end);
            } else if (line && !line.startsWith('WEBVTT') && !line.startsWith('NOTE') && currentSegment.startTime !== undefined) {
                const speakerMatch = line.match(/^([^:]+):\s*(.+)$/);
                if (speakerMatch) {
                    currentSegment.speaker = speakerMatch[1];
                    currentSegment.text = speakerMatch[2];
                    speakers.add(speakerMatch[1]);
                } else {
                    currentSegment.text = line;
                }
                
                if (currentSegment.startTime !== undefined && currentSegment.endTime !== undefined && currentSegment.text) {
                    segments.push({
                        id: `segment_${segments.length}`,
                        startTime: currentSegment.startTime,
                        endTime: currentSegment.endTime,
                        text: currentSegment.text,
                        speaker: currentSegment.speaker
                    });
                    currentSegment = {};
                }
            }
        }
    } else {
        const lines = content.split('\n').filter(line => line.trim());
        segments.push({
            id: 'segment_0',
            startTime: 0,
            endTime: 0,
            text: lines.join(' '),
            speaker: undefined
        });
    }

    const fullText = segments.map(s => s.text).join(' ');
    
    return {
        segments,
        fullText,
        duration,
        speakers: Array.from(speakers),
        format
    };
}

function parseVTTTime(timeStr: string): number {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
        const [hours, minutes, seconds] = parts;
        return parseFloat(hours) * 3600 + parseFloat(minutes) * 60 + parseFloat(seconds);
    }
    return 0;
}

function formatTimestamp(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
}